import '@/lib/errorReporter';
import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'next-themes';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { Toaster } from '@/components/ui/sonner';
import { enableMapSet } from "immer";
import '@/index.css';
// Pages
import { HomePage } from '@/pages/HomePage';
import LoginPage from '@/pages/auth/LoginPage';
import RegisterPage from '@/pages/auth/RegisterPage';
import DashboardPage from '@/pages/DashboardPage';
import AgentsPage from '@/pages/AgentsPage';
import MarketplacePage from '@/pages/MarketplacePage';
import SettingsPage from '@/pages/SettingsPage';
import AuditLogsPage from '@/pages/AuditLogsPage';
import PromptsPage from '@/pages/PromptsPage';
import ToolsPage from '@/pages/ToolsPage';
import IntegrationsPage from '@/pages/IntegrationsPage';
import EnterpriseRoadmapPage from '@/pages/EnterpriseRoadmapPage';
import PrivacyPage from '@/pages/legal/PrivacyPage';
import TermsPage from '@/pages/legal/TermsPage';
import HelpPage from '@/pages/support/HelpPage';
import HandoverPage from '@/pages/docs/HandoverPage';
import AgentsDocPage from '@/pages/docs/AgentsDocPage';
import HardeningReportPage from '@/pages/docs/HardeningReportPage';
enableMapSet();
const queryClient = new QueryClient();
const router = createBrowserRouter([
  {
    path: "/",
    element: <HomePage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/privacy",
    element: <PrivacyPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/terms",
    element: <TermsPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/help",
    element: <HelpPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/docs",
    element: <HandoverPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/docs/agents",
    element: <AgentsDocPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/docs/hardening",
    element: <HardeningReportPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/login",
    element: <LoginPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/register",
    element: <RegisterPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/dashboard",
    element: (
      <ProtectedRoute>
        <DashboardPage />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/agents",
    element: (
      <ProtectedRoute>
        <AgentsPage />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/marketplace",
    element: (
      <ProtectedRoute>
        <MarketplacePage />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/audit",
    element: (
      <ProtectedRoute>
        <AuditLogsPage />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/prompts",
    element: (
      <ProtectedRoute>
        <PromptsPage />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/tools",
    element: (
      <ProtectedRoute>
        <ToolsPage />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/integrations",
    element: (
      <ProtectedRoute>
        <IntegrationsPage />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/enterprise/roadmap",
    element: (
      <ProtectedRoute>
        <EnterpriseRoadmapPage />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/settings",
    element: (
      <ProtectedRoute>
        <SettingsPage />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  }
]);
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <ErrorBoundary>
          <RouterProvider router={router} />
          <Toaster richColors position="top-right" closeButton />
        </ErrorBoundary>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>
);